import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface CategoryStatic {
  id: string;
  number: string;
  icon: string;
  color: string;
  stat1Value: string;
  stat2Value: string;
  images: string[];
  imageLabels?: string[];
  imageObjectPositions?: string[];
}

const IMAGE_DURATION_MS = 4000;

const categoryStatic: CategoryStatic[] = [
  {
    id: "campanas",
    number: "01",
    icon: "ri-megaphone-line",
    color: "#ed0578",
    stat1Value: "340%",
    stat2Value: "120+",
    images: [
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/b99cf6ca-4c9e-4301-b06d-5c482066c3dd_compressed_eveleyn-garnier.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/d1555b5b-0a9f-45cf-8f71-2cd9f815ecda_compressed_sebasti-f1.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/46435685-e93a-4364-a697-4eec50ae3245_compressed_vale-maybelline.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/f1192abe-66b0-4526-9f36-33e194674c6f_compressed_Sephora-13.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/12d97d78-b8cb-460d-8ea3-73005689c156_compressed_emily.webp",
    ],
    imageLabels: ["Evelyn · Garnier", "Sebasti · F1", "Vale · Maybelline", "Sephora · Get Ready House", "Emily · Nestlé"],
  },
  {
    id: "eventos",
    number: "02",
    icon: "ri-calendar-event-line",
    color: "#d0f855",
    stat1Value: "5,000+",
    stat2Value: "48",
    images: [
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/e5e968a7-0704-40de-8719-1b3ce0cdc88d_compressed_pandora.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/00cd3aa7-85a7-41b3-9472-110758d1835d_compressed_aitana.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/791b1d46-b978-4d82-aeff-e8afe8b2cb18_compressed_7.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/c33e848b-bd01-4c9b-b8c5-045f9940c5f3_compressed_TeddyTint-16.webp",
    ],
    imageLabels: ["Pandora", "Aitana", "DiDi Card", "Evento"],
  },
  {
    id: "entrevistas",
    number: "03",
    icon: "ri-mic-line",
    color: "#ff6b35",
    stat1Value: "2.1M",
    stat2Value: "800+",
    images: [
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/50bbd40b-9d0d-478a-a5cf-c9124ddd0f76_compressed_ted1.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/d442c6ab-3fbb-4f67-b66d-22b123c972b6_compressed_fenal.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/e1342638-589d-4b4e-8e86-3e419b44e2a9_compressed_abi.webp",
    ],
    imageLabels: ["Ted · Universal", "Fenal · León", "Abi"],
    imageObjectPositions: ["50% 50%", "50% 50%", "50% 18%"],
  },
  {
    id: "embajadores",
    number: "04",
    icon: "ri-user-star-line",
    color: "#ffc93c",
    stat1Value: "94%",
    stat2Value: "60+",
    images: [
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/59dca6c3-0c9e-4ced-8845-f63f8d9c34d1_compressed_TeddyTint-19.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/d63c7fa0-eec5-4af9-9a64-fcceb26fa938_compressed_ob-evelyn.webp",
      "https://storage.helloreaddy.io/project_files/9d47c388-655f-4be5-bf40-a8a867a3d101/51d14c14-fa9c-4521-bb33-48f7f38cc40a_compressed_SUPERSTAY-3.webp",
    ],
    imageLabels: ["Teddy Tint · Maybelline", "Evelyn · Openbank", "Superstay · Maybelline"],
    imageObjectPositions: ["center 22%", "center 20%", "center 32%"],
  },
];

interface CategoryText {
  tag: string;
  subtitle: string;
  title: string;
  desc: string;
  highlights: string[];
  stat1Label: string;
  stat2Label: string;
}

export default function EnfoquesSection() {
  const { t, i18n } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);
  const imgAutoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const categories = useMemo(() => {
    const text = t("enfoques.categories", { returnObjects: true }) as unknown as CategoryText[];
    return categoryStatic.map((s, i) => ({ ...s, ...text[i] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const current = categories[activeIdx];

  // Manual section change — always restarts on the first photo
  const switchCategory = useCallback((idx: number) => {
    if (idx === activeIdx) return;
    setActiveIdx(idx);
    setImgIdx(0);
  }, [activeIdx]);

  // Keep the latest image index readable from inside the interval
  const imgIdxRef = useRef(0);
  useEffect(() => { imgIdxRef.current = imgIdx; }, [imgIdx]);

  // Auto-advance photos. When every photo of the current section has been shown,
  // move to the NEXT section with a clean cross-fade, always starting from its first photo.
  useEffect(() => {
    imgAutoRef.current = setInterval(() => {
      const lastIdx = current.images.length - 1;
      if (imgIdxRef.current >= lastIdx) {
        setActiveIdx((ci) => (ci + 1) % categories.length);
        setImgIdx(0);
      } else {
        setImgIdx((prev) => prev + 1);
      }
    }, IMAGE_DURATION_MS);
    return () => { if (imgAutoRef.current) clearInterval(imgAutoRef.current); };
  }, [current, categories.length]);

  return (
    <section
      id="enfoques"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh", backgroundColor: "#0d0b1e" }}
    >
      {/* Dark overlay gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(100deg, rgba(13,11,30,0.97) 0%, rgba(13,11,30,0.85) 45%, rgba(13,11,30,0.25) 100%)",
          zIndex: 2,
        }}
      />
      {/* Color glow that follows the active category */}
      <div
        className="absolute pointer-events-none transition-all duration-700"
        style={{
          zIndex: 2,
          top: "8%",
          left: "30%",
          width: "46vw",
          height: "46vw",
          background: `radial-gradient(circle, ${current.color}1f 0%, transparent 68%)`,
        }}
      />
      {/* Bottom vignette */}
      <div
        className="absolute bottom-0 left-0 right-0 h-40"
        style={{
          background: "linear-gradient(to top, rgba(13,11,30,0.95), transparent)",
          zIndex: 2,
        }}
      />

      {/* === MAIN CONTENT === */}
      <div className="relative z-10 flex flex-col min-h-screen px-5 md:px-16 py-16 md:py-20">

        {/* Top label */}
        <div className="flex items-center gap-3 mb-12">
          <span className="text-white/45 text-xs uppercase tracking-[0.25em] font-bold">{t("enfoques.label")}</span>
          <span className="flex-1 h-px" style={{ backgroundColor: "rgba(255,255,255,0.12)" }}></span>
          {/* Image dots */}
          <div className="flex gap-2">
            {current.images.map((_, ii) => (
              <button
                key={ii}
                onClick={() => setImgIdx(ii)}
                className="rounded-full transition-all duration-300 cursor-pointer"
                style={{
                  width: ii === imgIdx ? "24px" : "8px",
                  height: "8px",
                  backgroundColor: ii === imgIdx ? current.color : "rgba(255,255,255,0.25)",
                }}
              />
            ))}
          </div>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row flex-1 gap-6 lg:gap-0 items-center">

          {/* LEFT: Category selector tabs */}
          <div className="hidden lg:flex lg:flex-col gap-3 lg:mr-14 justify-center shrink-0">
            {categories.map((cat, ci) => {
              const isActive = ci === activeIdx;
              return (
                <button
                  key={cat.id}
                  onClick={() => switchCategory(ci)}
                  className="group relative flex items-center gap-3 cursor-pointer transition-all duration-300 whitespace-nowrap"
                  style={{
                    padding: "13px 18px",
                    borderRadius: "14px",
                    backgroundColor: isActive ? cat.color : "rgba(255,255,255,0.04)",
                    border: isActive
                      ? "1.5px solid rgba(255,255,255,0.18)"
                      : "1.5px solid rgba(255,255,255,0.08)",
                    minWidth: "186px",
                  }}
                >
                  <span
                    className="text-sm font-bold tracking-wide"
                    style={{ color: isActive ? "#0d0b1e" : "rgba(255,255,255,0.45)" }}
                  >
                    {cat.tag}
                  </span>
                  {isActive && (
                    <span className="ml-auto w-3 h-3 flex items-center justify-center" style={{ color: "#0d0b1e" }}>
                      <i className="ri-arrow-right-line text-base"></i>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* CENTER: Main content area */}
          <div className="flex-1 min-w-0 flex flex-col justify-center order-1 lg:order-2">

            <div key={activeIdx} className="animate-section-reveal">
            {/* Category badge */}
            <div className="flex items-center gap-3 mb-6">
              <span
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-black tracking-wide uppercase"
                style={{
                  backgroundColor: `${current.color}22`,
                  color: current.color,
                  border: `1.5px solid ${current.color}66`,
                }}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={current.icon}></i>
                </span>
                {current.tag}
              </span>
              <span className="text-white/35 text-xs font-medium">{current.subtitle}</span>
            </div>

            {/* Title */}
            <div>
              <h2
                className="font-black leading-[0.98] mb-6 tracking-tight break-words"
                style={{
                  fontSize: "clamp(30px, 4.4vw, 74px)",
                  color: "#fff",
                  maxWidth: "820px",
                }}
              >
                {current.title}
              </h2>
            </div>
            </div>
          </div>

          {/* Category pill nav (mobile/tablet) — above the photos */}
          <div className="lg:hidden order-2 w-full flex gap-2 justify-center flex-wrap">
            {categories.map((cat, ci) => (
              <button
                key={cat.id}
                onClick={() => switchCategory(ci)}
                className="px-4 py-2 rounded-full text-xs font-bold cursor-pointer transition-all duration-300 whitespace-nowrap"
                style={{
                  backgroundColor: ci === activeIdx ? cat.color : "rgba(255,255,255,0.08)",
                  color: ci === activeIdx ? "#0d0b1e" : "rgba(255,255,255,0.5)",
                }}
              >
                {cat.tag}
              </button>
            ))}
          </div>

          {/* RIGHT: Editorial image frame (cross-fades between photos and sections) */}
          <div
            className="relative w-full lg:w-[44%] h-[120vw] max-h-[560px] sm:h-[80vw] lg:h-[min(80vh,720px)] lg:max-h-none shrink-0 order-3 overflow-hidden rounded-2xl border border-white/10 lg:ml-12"
            style={{ backgroundColor: "#141126" }}
          >
            {/* Blurred backdrop of the active photo */}
            <div className="absolute inset-0">
              {current.images.map((img, ii) => (
                <img
                  key={`bg-${ii}`}
                  src={img}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
                  style={{
                    opacity: ii === imgIdx ? 1 : 0,
                    filter: "blur(36px) saturate(1.15) brightness(0.42)",
                    transform: "scale(1.3)",
                  }}
                />
              ))}
            </div>
            {/* Photos of every section, stacked so the change is a smooth cross-fade */}
            {categories.map((cat, ci) =>
              cat.images.map((img, ii) => (
                <img
                  key={`fg-${ci}-${ii}`}
                  src={img}
                  alt={cat.imageLabels?.[ii] || `${cat.tag} - 301+ Agency`}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
                  style={{ opacity: ci === activeIdx && ii === imgIdx ? 1 : 0, objectPosition: cat.imageObjectPositions?.[ii] || "center" }}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}